import { useState, useEffect, useCallback, useContext } from 'react';
import { Box, Typography, Stack, Chip, Button, Avatar } from '@mui/joy';
import { motion, AnimatePresence } from 'framer-motion';

import { AppContext } from '../context';
import {
    APIGetBirthdaysToday,
    APIGetBirthdayRemindersActive,
    APIAckBirthdayReminder,
} from '../api';

const fmtDayMonth = (iso) => {
    if (!iso) return '';
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
};

const daysUntil = (iso) => {
    if (!iso) return null;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const d = new Date(iso + 'T00:00:00');
    return Math.round((d - today) / 86400000);
};

export default function BirthdayWidget() {
    const { token, showToast, onRefreshTick } = useContext(AppContext);

    const [today, setToday]         = useState([]);   // именинники сегодня
    const [reminders, setReminders] = useState([]);   // активные напоминания (не отмеченные)
    const [ackingId, setAckingId]   = useState(null);
    const [loaded, setLoaded]       = useState(false);

    const load = useCallback(async () => {
        if (!token) return;
        const [todayRes, remRes] = await Promise.all([
            APIGetBirthdaysToday(token),
            APIGetBirthdayRemindersActive(token),
        ]);
        setToday(Array.isArray(todayRes?.birthdays) ? todayRes.birthdays : []);
        setReminders(Array.isArray(remRes) ? remRes.filter(r => !r.acknowledged) : []);
        setLoaded(true);
    }, [token]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => onRefreshTick(load), [onRefreshTick, load]);

    const handleAck = async (id) => {
        if (ackingId) return;
        setAckingId(id);
        const res = await APIAckBirthdayReminder(token, id);
        setAckingId(null);
        if (res?.error) { showToast('Не удалось отметить', 'danger'); return; }
        setReminders(prev => prev.filter(r => r.id !== id)); // убираем у себя
    };

    // Пока не загрузились или показывать нечего — ничего не рисуем
    if (!loaded) return null;
    if (today.length === 0 && reminders.length === 0) return null;

    return (
        <Box sx={{ mb: 3 }}>
            {/* Поздравление сегодня */}
            {today.length > 0 && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
                    <Box sx={{
                        mb: reminders.length > 0 ? 1.5 : 0,
                        p: { xs: 2, sm: 2.5 },
                        borderRadius: 'var(--joy-radius-xl)',
                        border: '1px solid rgba(245,158,11,0.35)',
                        background: 'linear-gradient(135deg, rgba(245,158,11,0.12) 0%, rgba(162,38,226,0.10) 100%)',
                    }}>
                        {today.map(p => (
                            <Box key={p.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.4 }}>
                                <Avatar src={p.avatar_url || undefined} size="sm" sx={{
                                    width: 40, height: 40, fontSize: 15, fontWeight: 800,
                                    bgcolor: 'rgba(245,158,11,0.20)', color: '#f59e0b',
                                    border: '1.5px solid rgba(245,158,11,0.6)', flexShrink: 0,
                                }}>
                                    {(p.full_name || '').charAt(0).toUpperCase()}
                                </Avatar>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography sx={{ fontSize: 16, fontWeight: 800, color: 'var(--joy-palette-text-primary)' }}>
                                        {p.is_me ? 'С днём рождения! 🎉' : 'Сегодня день рождения 🎉'}
                                    </Typography>
                                    <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)' }}>
                                        {p.is_me ? 'Пусть всё получается!' : p.full_name}
                                    </Typography>
                                </Box>
                                <Box component="span" sx={{ fontSize: 26, flexShrink: 0 }}>🎂</Box>
                            </Box>
                        ))}
                    </Box>
                </motion.div>
            )}

            {/* Напоминания «скоро день рождения» */}
            <AnimatePresence initial={false}>
                {reminders.map(r => {
                    const dleft = daysUntil(r.birthday);
                    const whenTxt = dleft === 0 ? 'сегодня'
                        : dleft === 1 ? 'завтра'
                        : (dleft != null ? `через ${dleft} дн.` : '');
                    return (
                        <motion.div
                            key={r.id}
                            layout
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                            transition={{ duration: 0.25 }}
                        >
                            <Box sx={{
                                mb: 1,
                                p: { xs: 1.5, sm: 2 },
                                borderRadius: 'var(--joy-radius-lg)',
                                border: '1px solid var(--joy-palette-divider)',
                                bgcolor: 'var(--joy-palette-background-level1)',
                                display: 'flex', alignItems: 'center', gap: 1.5,
                            }}>
                                <Avatar src={r.person_avatar_url || undefined} size="sm" sx={{
                                    width: 36, height: 36, fontSize: 13, fontWeight: 700,
                                    bgcolor: 'rgba(162,38,226,0.15)', color: 'var(--joy-palette-primary-300)',
                                    border: '1.5px solid var(--joy-palette-primary-500)', flexShrink: 0,
                                }}>
                                    {(r.person_name || '').charAt(0).toUpperCase()}
                                </Avatar>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        🎂 {r.person_name}
                                    </Typography>
                                    <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-secondary)' }}>
                                        День рождения {fmtDayMonth(r.birthday)}{whenTxt ? ` · ${whenTxt}` : ''} — не забудьте поздравить
                                    </Typography>
                                </Box>
                                <Button
                                    size="sm"
                                    loading={ackingId === r.id}
                                    onClick={() => handleAck(r.id)}
                                    startDecorator={<Box component="span" sx={{ fontWeight: 900 }}>✓</Box>}
                                    sx={{
                                        flexShrink: 0, fontWeight: 700, fontSize: 12,
                                        bgcolor: 'rgba(34,197,94,0.14)', color: '#22c55e',
                                        '&:hover': { bgcolor: 'rgba(34,197,94,0.22)' },
                                    }}
                                >
                                    Принято
                                </Button>
                            </Box>
                        </motion.div>
                    );
                })}
            </AnimatePresence>
        </Box>
    );
}